// ─── Auditoría y bitácora ────────────────────────────────────────────────────
// Dos maneras de leer `public.auditoria` (lo que escribe src/core/auditLog.js):
//
//  · La BITÁCORA de un registro (📜): la línea de tiempo de un documento, un
//    cliente, un producto, una configuración o un usuario. Se abre desde el
//    propio registro, como una capa encima del formulario (no lo cierra), y
//    dice en palabras qué cambió cada vez y quién lo hizo.
//  · La página AURA → Administración → Auditoría: todo lo que pasó, con
//    filtros por qué, quién, cuándo y qué tipo de acción.
//
// La lectura en palabras (diferencias, agrupación del autoguardado) vive en
// src/core/bitacora.js, que es puro y tiene sus pruebas.
import { tienePermiso } from "../auth/session.js";
import {
  CONFIGURACIONES, ENTIDADES, EVENTOS, diffRegistro, etiquetaAccion, etiquetaEntidad, lineaDeTiempo, resumenFila
} from "../core/bitacora.js";
import { C, DOC_INFO } from "../core/config.js";
import { state } from "../core/state.js";
import { cloudState, supa } from "../core/supabaseClient.js";
import { btnStyle, escapeHTML, inputStyle } from "../core/utils.js";
import { render } from "../ui/app.js";

const COLUMNAS = "id,fecha,entidad,entidad_id,accion,evento,referencia,usuario_email,usuario_nombre,datos_antes,datos_despues";
const ctxNombres = () => ({ clientes: (state.data && state.data.clientes) || [], proveedores: (state.data && state.data.proveedores) || [] });
const fechaHora = (f) => { try { return new Date(f).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }); } catch { return f || ""; } };
// Un valor dentro de un filtro `or` de PostgREST va entre comillas.
const citar = (v) => `"${String(v).replace(/"/g, '\\"')}"`;

// ¿Quién puede leer la bitácora? Quien entra a Auditoría (los administradores,
// o los roles a los que se les abrió esa página).
export const puedeVerBitacora = () => tienePermiso("auditoria");

// El botón 📜 que va en el encabezado de un registro. Vacío si el registro aún
// no existe (un formulario nuevo no tiene historia) o si no hay permiso.
export function botonBitacora(entidad, id, titulo, opts = {}) {
  if (!id || !puedeVerBitacora()) return "";
  return `<button data-action="ver-bitacora" data-entidad="${escapeHTML(entidad)}" data-id="${escapeHTML(String(id))}"
    data-titulo="${escapeHTML(titulo || "")}" data-referencia="${escapeHTML(opts.referencia || "")}"
    style="${btnStyle(null, "ghost", true)}white-space:nowrap;" title="Quién creó, cambió o usó este registro, y cuándo">📜 Bitácora</button>`;
}

// ─── Consultas ───────────────────────────────────────────────────────────────
export async function cargarBitacoraRegistro(entidad, id, referencia) {
  if (!cloudState.tenantId) return { filas: [], error: "No hay sesión activa." };
  let q = supa.from("auditoria").select(COLUMNAS).eq("tenant_id", cloudState.tenantId).eq("entidad", entidad);
  // Un documento se sigue también por su NÚMERO: un rechazo de la DIAN queda a
  // nombre del consecutivo intentado (la factura aún no existe), y un
  // documento borrado y vuelto a crear con el mismo número es la misma historia.
  q = referencia && ENTIDADES[entidad] && ENTIDADES[entidad].tipo
    ? q.or(`entidad_id.eq.${citar(id)},referencia.eq.${citar(referencia)}`)
    : q.eq("entidad_id", String(id));
  const { data, error } = await q.order("fecha", { ascending: true }).limit(1500);
  if (error) return { filas: [], error: error.message };
  return { filas: data || [] };
}

export async function cargarActividadUsuario(email, id) {
  if (!cloudState.tenantId) return { filas: [], error: "No hay sesión activa." };
  let q = supa.from("auditoria").select(COLUMNAS).eq("tenant_id", cloudState.tenantId);
  // Lo que hizo la persona y lo que un administrador le hizo a su cuenta.
  q = id ? q.or(`usuario_email.eq.${citar(email)},and(entidad.eq.usuarios,entidad_id.eq.${citar(id)})`) : q.eq("usuario_email", email);
  const { data, error } = await q.order("fecha", { ascending: false }).limit(500);
  if (error) return { filas: [], error: error.message };
  return { filas: data || [] };
}

export async function cargarAuditoria(filtros) {
  if (!cloudState.tenantId) return [];
  let query = supa.from("auditoria").select(COLUMNAS).eq("tenant_id", cloudState.tenantId).order("fecha", { ascending: false }).limit(300);
  if (filtros.entidad) query = query.eq("entidad", filtros.entidad);
  if (filtros.accion === "evento") query = query.eq("accion", "evento");
  else if (filtros.accion) query = query.eq("accion", filtros.accion);
  if (filtros.referencia) query = query.ilike("referencia", `%${filtros.referencia}%`);
  if (filtros.usuario) query = query.or(`usuario_email.ilike.${citar("%" + filtros.usuario + "%")},usuario_nombre.ilike.${citar("%" + filtros.usuario + "%")}`);
  if (filtros.desde) query = query.gte("fecha", filtros.desde);
  if (filtros.hasta) query = query.lte("fecha", filtros.hasta + "T23:59:59");
  const { data, error } = await query;
  if (error) { console.error("⚠️ Error cargando auditoría:", error.message); return []; }
  return data || [];
}

// ─── La capa de la bitácora ──────────────────────────────────────────────────
// Va pegada al <body>, fuera de #root: render() reconstruye #root en cada clic
// y la bitácora se abre ENCIMA de un formulario que no debe cerrarse.
const pila = []; // para «← Volver» al navegar de un documento a su origen
let actual = null;

function capa() {
  let el = document.getElementById("aura-bitacora");
  if (!el) {
    el = document.createElement("div");
    el.id = "aura-bitacora";
    el.style.cssText = "position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1300;padding:11px;";
    el.addEventListener("click", (e) => { if (e.target === el) cerrarBitacora(); });
    document.body.appendChild(el);
  }
  return el;
}

export function cerrarBitacora() {
  const el = document.getElementById("aura-bitacora");
  if (el) el.remove();
  pila.length = 0; actual = null;
}

// Escape cierra primero la bitácora (está encima), y no el formulario de abajo.
if (typeof document !== "undefined" && !window.__auraBitacoraEsc) {
  window.__auraBitacoraEsc = true;
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && document.getElementById("aura-bitacora")) {
      e.stopImmediatePropagation(); e.preventDefault(); cerrarBitacora();
    }
  }, true);
}

function marco(titulo, subtitulo, cuerpo) {
  return `
    <div style="background:white;border-radius:14px;width:760px;max-width:100%;max-height:88vh;max-height:88dvh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.3);">
      <div style="padding:10px 14px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;gap:8px;background:#f8fafc;border-radius:14px 14px 0 0;">
        <div style="min-width:0;">
          <h3 style="margin:0;font-size:15px;color:${C.text};font-weight:800;">📜 ${escapeHTML(titulo)}</h3>
          <div style="font-size:10.5px;color:${C.muted};margin-top:2px;">${subtitulo}</div>
        </div>
        <div style="display:flex;gap:6px;align-items:center;flex:0 0 auto;">
          ${pila.length ? `<button data-action="bitacora-volver" style="${btnStyle(null, "secondary", true)}">← Volver</button>` : ""}
          <button data-action="cerrar-bitacora" style="background:transparent;border:none;font-size:17px;color:${C.muted};cursor:pointer;padding:2px 6px;" title="Cerrar">✕</button>
        </div>
      </div>
      <div data-bitacora-cuerpo style="padding:12px 14px;overflow-y:auto;">${cuerpo}</div>
    </div>`;
}

// Lo que nació de este documento y de dónde vino, para seguir el hilo.
function hiloDocumento(entidad, id) {
  const tipo = ENTIDADES[entidad] && ENTIDADES[entidad].tipo;
  if (!tipo) return "";
  const doc = ((state.data && state.data[entidad]) || []).find(d => d.id === id);
  if (!doc) return "";
  const enlace = (t, d) => {
    const info = DOC_INFO[t] || {};
    return `<button data-action="ver-bitacora" data-entidad="${info.key}" data-id="${escapeHTML(d.id)}" data-titulo="${escapeHTML(d.numero || "")}"
      data-referencia="${escapeHTML(d.numero || "")}" data-apilar="1"
      style="background:white;border:1px solid ${C.border};border-radius:14px;padding:2px 9px;font-size:10.5px;font-weight:700;color:${info.color || C.text};cursor:pointer;">
      ${info.icon || ""} ${escapeHTML(d.numero || "?")}</button>`;
  };
  const buscar = (t, refId) => ((state.data && state.data[(DOC_INFO[t] || {}).key]) || []).find(x => x.id === refId);
  const origenes = [doc._origen, ...(Array.isArray(doc._origenes) ? doc._origenes : [])].filter(o => o && o.tipo && o.id);
  const vistos = new Set();
  const padres = origenes.filter(o => !vistos.has(o.tipo + o.id) && vistos.add(o.tipo + o.id))
    .map(o => { const d = buscar(o.tipo, o.id); return d ? enlace(o.tipo, d) : `<span style="font-size:10.5px;color:${C.muted};">${escapeHTML(o.numero || "?")} (ya no existe)</span>`; });
  const hijos = [];
  Object.keys(DOC_INFO).forEach(t => {
    ((state.data && state.data[DOC_INFO[t].key]) || []).forEach(x => {
      const de = [x._origen, ...(Array.isArray(x._origenes) ? x._origenes : [])];
      if (de.some(o => o && o.tipo === tipo && o.id === id)) hijos.push(enlace(t, x));
    });
  });
  if (!padres.length && !hijos.length) return "";
  return `
    <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:8px;padding:8px 10px;margin-bottom:12px;font-size:11px;color:${C.text};display:grid;gap:6px;">
      ${padres.length ? `<div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;"><b style="color:${C.muted};font-size:10px;">VIENE DE</b> ${padres.join(" ")}</div>` : ""}
      ${hijos.length ? `<div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;"><b style="color:${C.muted};font-size:10px;">GENERÓ</b> ${hijos.join(" ")}</div>` : ""}
    </div>`;
}

// Una entrada de la línea de tiempo.
function entradaHTML(g, conRegistro) {
  const rango = g.veces > 1
    ? `${fechaHora(g.desde)} → ${new Date(g.hasta).toLocaleTimeString("es-CO", { timeStyle: "short" })}`
    : fechaHora(g.desde);
  const max = 14;
  const cambios = g.cambios || [];
  const lista = (cs) => cs.map(c => {
    const color = c.tipo === "agrega" ? "#047857" : c.tipo === "quita" ? "#b91c1c" : C.text;
    return `<li style="margin:2px 0;color:${color};"><span style="color:${C.muted};">${escapeHTML(c.etiqueta)}:</span> ${escapeHTML(c.texto)}</li>`;
  }).join("");
  const registro = conRegistro
    ? `<span style="font-size:10.5px;color:${C.muted};">${(ENTIDADES[g.entidad] || {}).icon || ""} ${escapeHTML(etiquetaEntidad(g.entidad))}${g.referencia ? " · <b style=\"color:" + C.text + ";\">" + escapeHTML(g.referencia) + "</b>" : ""}</span>`
    : "";
  return `
    <div data-bitacora-entrada="${escapeHTML(g.evento || g.accion)}" style="display:grid;grid-template-columns:26px 1fr;gap:8px;padding:8px 0;border-bottom:1px solid ${C.border};">
      <div style="width:26px;height:26px;border-radius:50%;background:${g.color}18;color:${g.color};display:flex;align-items:center;justify-content:center;font-size:13px;">${g.icon}</div>
      <div style="min-width:0;">
        <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:baseline;">
          <div style="font-size:12px;font-weight:800;color:${g.color};">${escapeHTML(g.label)}${g.veces > 1 ? ` <span style="font-weight:600;color:${C.muted};font-size:10.5px;">(${g.veces} guardados)</span>` : ""}</div>
          <div style="font-size:10.5px;color:${C.muted};white-space:nowrap;">${escapeHTML(rango)}</div>
        </div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:1px;">por <b style="color:${C.text};">${escapeHTML(g.usuario)}</b> ${registro}</div>
        ${cambios.length ? `<ul style="margin:5px 0 0;padding-left:16px;font-size:11px;line-height:1.45;">${lista(cambios.slice(0, max))}</ul>` : ""}
        ${cambios.length > max ? `<details style="margin-top:3px;"><summary style="font-size:10.5px;color:${C.blue || "#2563eb"};cursor:pointer;">y ${cambios.length - max} cambio${cambios.length - max === 1 ? "" : "s"} más</summary><ul style="margin:3px 0 0;padding-left:16px;font-size:11px;line-height:1.45;">${lista(cambios.slice(max))}</ul></details>` : ""}
      </div>
    </div>`;
}

function cuerpoLinea(filas, conRegistro) {
  const { entradas, sinCambios } = lineaDeTiempo(filas, ctxNombres());
  if (!entradas.length) {
    return `<div style="padding:18px;text-align:center;color:${C.muted};font-size:11.5px;">Aún no hay nada registrado.${sinCambios ? ` (${sinCambios} guardado${sinCambios === 1 ? "" : "s"} sin cambios de contenido.)` : ""}
      <div style="font-size:10.5px;margin-top:6px;">La bitácora empezó a llevarse con la auditoría: lo anterior no quedó escrito.</div></div>`;
  }
  return entradas.map(g => entradaHTML(g, conRegistro)).join("") +
    (sinCambios ? `<div style="font-size:10px;color:${C.muted};padding-top:8px;">Además, ${sinCambios} guardado${sinCambios === 1 ? "" : "s"} automático${sinCambios === 1 ? "" : "s"} sin cambios de contenido, que no se muestra${sinCambios === 1 ? "" : "n"}.</div>` : "");
}

export function abrirBitacora({ entidad, id, titulo, referencia, apilar } = {}) {
  if (apilar && actual) pila.push(actual);
  else if (!apilar) pila.length = 0;
  return pintarRegistro({ modo: "registro", entidad, id, titulo, referencia });
}

async function pintarRegistro(reg) {
  actual = reg;
  const { entidad, id, titulo, referencia } = reg;
  const nombre = titulo || referencia || etiquetaEntidad(entidad);
  const sub = `${escapeHTML(etiquetaEntidad(entidad))} · quién lo creó, lo cambió o lo usó, y cuándo`;
  const el = capa();
  el.innerHTML = marco(nombre, sub, `<div style="padding:18px;text-align:center;color:${C.muted};font-size:11.5px;">Cargando la bitácora…</div>`);
  const { filas, error } = await cargarBitacoraRegistro(entidad, id, referencia);
  if (!document.getElementById("aura-bitacora") || actual !== reg) return; // se cerró o se cambió mientras cargaba
  el.innerHTML = marco(nombre, sub, error
    ? `<div style="color:#b91c1c;font-size:11.5px;">No se pudo leer la bitácora: ${escapeHTML(error)}</div>`
    : hiloDocumento(entidad, id) + cuerpoLinea(filas, false));
}

export async function abrirActividadUsuario({ email, id, nombre } = {}) {
  pila.length = 0;
  actual = { modo: "usuario", email, id, nombre };
  const sub = `${escapeHTML(email || "")} · lo que hizo en AURA y lo que se le hizo a su cuenta (últimos 500)`;
  const el = capa();
  el.innerHTML = marco(`Actividad de ${nombre || email}`, sub, `<div style="padding:18px;text-align:center;color:${C.muted};font-size:11.5px;">Cargando…</div>`);
  const { filas, error } = await cargarActividadUsuario(email, id);
  if (!document.getElementById("aura-bitacora") || !actual || actual.email !== email) return;
  el.innerHTML = marco(`Actividad de ${nombre || email}`, sub, error
    ? `<div style="color:#b91c1c;font-size:11.5px;">No se pudo leer la actividad: ${escapeHTML(error)}</div>`
    : cuerpoLinea(filas, true));
}

// ─── La página de Auditoría ──────────────────────────────────────────────────
const ACCIONES_FILTRO = [
  ["", "Todas"], ["create", "Creó"], ["update", "Editó"], ["delete", "Eliminó"], ["evento", "Procesos y eventos"]
];

export function renderAuditoria() {
  if (!state._auditoriaFiltros) state._auditoriaFiltros = { entidad: "", accion: "", referencia: "", usuario: "", desde: "", hasta: "" };
  if (state._auditoriaCache === undefined) {
    state._auditoriaCache = null;
    cargarAuditoria(state._auditoriaFiltros).then(rows => { state._auditoriaCache = rows; render(); });
  }
  const f = state._auditoriaFiltros;
  const filas = state._auditoriaCache;
  const entidades = Object.keys(ENTIDADES).sort((a, b) => etiquetaEntidad(a).localeCompare(etiquetaEntidad(b), "es"));
  const ctx = ctxNombres();

  const header = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:9px;padding-bottom:10px;border-bottom:3px solid ${C.gold};">
      <div style="display:flex;gap:10px;align-items:center;">
        <img src="${window.LOGO}" alt="Aura" style="height:42px;object-fit:contain;"/>
        <div>
          <h2 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">🕵️ Auditoría</h2>
          <p style="margin:3px 0 0;color:${C.muted};font-size:11.5px;">Quién creó, editó, eliminó o usó cada documento, maestro, configuración y usuario, y cuándo. Historial de solo lectura: no se puede modificar ni borrar.</p>
        </div>
      </div>
    </div>
  `;

  const campo = (label, html) => `<div><label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">${label}</label>${html}</div>`;
  const filtrosHTML = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:14px;display:flex;gap:9px;flex-wrap:wrap;align-items:flex-end;">
      ${campo("Qué", `<select id="aud-entidad" style="${inputStyle}min-width:170px;">
          <option value="">Todo</option>
          ${entidades.map(e => `<option value="${e}" ${f.entidad === e ? "selected" : ""}>${escapeHTML(etiquetaEntidad(e))}</option>`).join("")}
        </select>`)}
      ${campo("Acción", `<select id="aud-accion" style="${inputStyle}min-width:150px;">
          ${ACCIONES_FILTRO.map(([v, l]) => `<option value="${v}" ${f.accion === v ? "selected" : ""}>${l}</option>`).join("")}
        </select>`)}
      ${campo("Número o nombre", `<input id="aud-referencia" type="text" value="${escapeHTML(f.referencia || "")}" placeholder="ej: RS-0228, MP157, CROWN" style="${inputStyle}width:180px;"/>`)}
      ${campo("Usuario", `<input id="aud-usuario" type="text" value="${escapeHTML(f.usuario || "")}" placeholder="nombre o correo" style="${inputStyle}width:170px;"/>`)}
      ${campo("Desde", `<input id="aud-desde" type="date" value="${f.desde}" style="${inputStyle}width:145px;"/>`)}
      ${campo("Hasta", `<input id="aud-hasta" type="date" value="${f.hasta}" style="${inputStyle}width:145px;"/>`)}
      <button data-action="buscar-auditoria" style="${btnStyle(null, "secondary")}">🔍 Buscar</button>
    </div>
  `;

  const th = (t) => `<th style="text-align:left;padding:6px 10px;font-size:10.5px;color:${C.muted};border-bottom:1px solid ${C.border};">${t}</th>`;
  const td = (t, extra = "") => `<td style="padding:6px 10px;font-size:11.5px;color:${C.text};border-bottom:1px solid ${C.border};${extra}">${t}</td>`;
  const tablaHTML = filas === null ? `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;font-size:11.5px;color:${C.muted};">Cargando…</div>
  ` : filas.length === 0 ? `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;font-size:11.5px;color:${C.muted};">Sin registros para estos filtros.</div>
  ` : `
    <div style="font-size:10.5px;color:${C.muted};margin-bottom:6px;">${filas.length === 300 ? "Los 300 más recientes: afina los filtros para ver más atrás." : `${filas.length} registro${filas.length === 1 ? "" : "s"}.`}</div>
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:0;overflow-x:auto;">
      <table data-auditoria-tabla style="width:100%;border-collapse:collapse;min-width:860px;">
        <thead><tr>${th("Fecha")}${th("Qué")}${th("Acción")}${th("Usuario")}${th("Cambios")}<th style="border-bottom:1px solid ${C.border};"></th></tr></thead>
        <tbody>
          ${filas.map(r => {
            const a = etiquetaAccion(r);
            const ref = r.referencia || (r.entidad === "configuracion" ? CONFIGURACIONES[r.entidad_id] : "") || String(r.entidad_id).slice(0, 12);
            return `<tr data-auditoria-fila="${escapeHTML(r.evento || r.accion)}">
              ${td(escapeHTML(fechaHora(r.fecha)), "white-space:nowrap;")}
              ${td(`${(ENTIDADES[r.entidad] || {}).icon || ""} ${escapeHTML(etiquetaEntidad(r.entidad))}<div style="font-weight:700;">${escapeHTML(ref)}</div>`)}
              ${td(`<span style="color:${a.color};font-weight:700;">${a.icon} ${escapeHTML(a.label)}</span>`)}
              ${td(escapeHTML(r.usuario_nombre || r.usuario_email || "—"))}
              ${td(`<span style="color:${C.muted};font-size:10.5px;">${escapeHTML(resumenFila(r, ctx))}</span>`, "max-width:340px;")}
              <td style="padding:6px 10px;border-bottom:1px solid ${C.border};text-align:right;white-space:nowrap;">
                ${r.entidad === "sesion" ? "" : `<button data-action="ver-bitacora" data-entidad="${escapeHTML(r.entidad)}" data-id="${escapeHTML(String(r.entidad_id))}" data-titulo="${escapeHTML(ref)}" data-referencia="${escapeHTML(r.referencia || "")}" style="${btnStyle(null, "secondary", true)}" title="Toda la historia de este registro">📜</button>`}
                <button data-action="ver-detalle-auditoria" data-id="${r.id}" style="${btnStyle(null, "secondary", true)}">Ver cambios</button>
              </td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
    </div>
  `;

  return header + filtrosHTML + tablaHTML;
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "buscar-auditoria": async () => {
    const v = (id) => document.getElementById(id)?.value || "";
    state._auditoriaFiltros = {
      entidad: v("aud-entidad"), accion: v("aud-accion"), referencia: v("aud-referencia").trim(),
      usuario: v("aud-usuario").trim(), desde: v("aud-desde"), hasta: v("aud-hasta")
    };
    state._auditoriaCache = null;
    render();
    state._auditoriaCache = await cargarAuditoria(state._auditoriaFiltros);
    render();
  },
  "ver-bitacora": (d) => { abrirBitacora({ entidad: d.entidad, id: d.id, titulo: d.titulo, referencia: d.referencia, apilar: d.apilar === "1" }); },
  "bitacora-volver": () => {
    const previo = pila.pop();
    if (previo) pintarRegistro(previo);
  },
  "cerrar-bitacora": () => cerrarBitacora(),
  "ver-actividad-usuario": (d) => { abrirActividadUsuario({ email: d.email, id: d.id, nombre: d.nombre }); },
  "ver-detalle-auditoria": (d) => {
    const row = (state._auditoriaCache || []).find(r => r.id === d.id);
    if (!row) return;
    const a = etiquetaAccion(row);
    const cambios = row.accion === "update" ? diffRegistro(row.datos_antes, row.datos_despues, ctxNombres()) : null;
    const json = (v, vacio) => `<pre style="background:#f8fafc;border-radius:6px;padding:10px;font-size:10px;overflow-x:auto;white-space:pre-wrap;word-break:break-word;max-height:320px;">${escapeHTML(v ? JSON.stringify(v, null, 2) : vacio)}</pre>`;
    const ev = row.accion === "evento" ? (EVENTOS[row.evento] || {}) : null;
    state.modal = `
      <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
        <div data-action="prevent-close" style="background:white;border-radius:14px;width:760px;max-width:100%;max-height:85vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
          <div style="padding:10px 12px;border-bottom:1px solid ${C.border};">
            <h3 style="margin:0;font-size:13px;color:${a.color};font-weight:800;">${a.icon} ${escapeHTML(a.label)} · ${escapeHTML(etiquetaEntidad(row.entidad))} ${escapeHTML(row.referencia || String(row.entidad_id))}</h3>
            <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${escapeHTML(row.usuario_nombre || row.usuario_email || "—")} · ${escapeHTML(fechaHora(row.fecha))}</div>
          </div>
          <div style="padding:11px;">
            ${cambios ? (cambios.length
              ? `<ul style="margin:0 0 10px;padding-left:16px;font-size:11.5px;line-height:1.5;">${cambios.map(c => `<li><span style="color:${C.muted};">${escapeHTML(c.etiqueta)}:</span> ${escapeHTML(c.texto)}</li>`).join("")}</ul>`
              : `<div style="font-size:11.5px;color:${C.muted};margin-bottom:10px;">Guardado sin cambios de contenido.</div>`) : ""}
            ${ev ? json(row.datos_despues, "(sin detalle)") : `
              <details ${cambios ? "" : "open"}>
                <summary style="font-size:10.5px;font-weight:700;color:${C.muted};cursor:pointer;margin-bottom:6px;">Datos completos (antes y después)</summary>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
                  <div><div style="font-size:10.5px;font-weight:700;color:${C.muted};margin-bottom:6px;">ANTES</div>${json(row.datos_antes, "(no existía)")}</div>
                  <div><div style="font-size:10.5px;font-weight:700;color:${C.muted};margin-bottom:6px;">DESPUÉS</div>${json(row.datos_despues, "(eliminado)")}</div>
                </div>
              </details>`}
          </div>
          <div style="padding:14px 20px;border-top:1px solid ${C.border};text-align:right;">
            <button data-action="close-modal" style="${btnStyle(null, "secondary")}">Cerrar</button>
          </div>
        </div>
      </div>
    `;
    render();
  },
});
